import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Stack,
    Chip,
    Tooltip,
    InputAdornment,
    Button,
    IconButton,
    TablePagination,
} from "@mui/material";
import GenericDataTable from "../components/GenericDataTable";
import EmailDetailModal from "../components/EmailDetailModal";
import {
    getClientsForFilter,
    getAllReports,
} from "../Services/sauvegardesService";
import { useToast } from "../context/ToastContext";
import { useEmailDetailModal } from "../hooks/useEmailDetailModal";
import { formatDate } from "../utils/date";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";

/** Date locale au format YYYY-MM-DD (champ type="date"). */
function getLocalDateString(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function getYesterdayLocalDateString() {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return getLocalDateString(d);
}

export default function ReportsPage() {
    const [reports, setReports] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Filtres
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterClient, setFilterClient] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState(() => getYesterdayLocalDateString());
    const [filterDateTo, setFilterDateTo] = useState(() => getYesterdayLocalDateString());
    const { emailModalOpen, selectedJob, selectedReportData, selectedReportId, handleViewEmail, closeEmailModal } = useEmailDetailModal();

    const { showToast } = useToast();

    const statusOptions = [
        { value: "OK", label: "OK", color: "success" },
        { value: "WARNING", label: "WARNING", color: "warning" },
        { value: "FAILED", label: "FAILED", color: "error" },
        { value: "UNKNOWN", label: "UNKNOWN", color: "default" },
    ];

    const showToastRef = useRef(showToast);
    useEffect(() => { showToastRef.current = showToast; }, [showToast]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    const isFirstDebouncedSearch = useRef(true);
    useEffect(() => {
        if (isFirstDebouncedSearch.current) {
            isFirstDebouncedSearch.current = false;
            return;
        }
        setPage(0);
    }, [debouncedSearch]);

    const loadReports = useCallback(async () => {
        setLoading(true);
        try {
            const reportsRes = await getAllReports({
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                clientId: filterClient || undefined,
                statusTypeName: filterStatus || undefined,
                search: debouncedSearch || undefined,
                dateFrom: filterDateFrom || undefined,
                dateTo: filterDateTo || undefined,
                excludeToday: true,
            });
            setReports(Array.isArray(reportsRes?.data) ? reportsRes.data : []);
            setTotal(reportsRes?.total ?? 0);
        } catch (e) {
            console.error("Error loading reports:", e);
            showToastRef.current("Erreur lors du chargement des rapports", { severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filterClient, filterStatus, debouncedSearch, filterDateFrom, filterDateTo]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    useEffect(() => {
        let isMounted = true;

        async function loadClients() {
            try {
                const clientsList = await getClientsForFilter();
                if (!isMounted) return;
                setClients(Array.isArray(clientsList) ? clientsList : []);
            } catch (e) {
                console.error("Error loading clients:", e);
            }
        }

        loadClients();
        return () => { isMounted = false; };
    }, []);

    // Map clientId → nom client
    const clientsMap = useMemo(() => {
        const map = {};
        clients.forEach((c) => {
            map[c.id] = c.nom || c.name || `Client #${c.id}`;
        });
        return map;
    }, [clients]);

    const handleResetFilters = () => {
        setSearchText("");
        setDebouncedSearch("");
        setFilterClient("");
        setFilterStatus("");
        setFilterDateFrom("");
        setFilterDateTo("");
        setPage(0);
    };

    // Colonnes — une ligne = un rapport
    const columns = [
        {
            field: "jobName",
            headerName: "Job de sauvegarde",
            renderCell: (row) => row.jobName || `Job #${row.jobId}`,
        },
        {
            field: "clientName",
            headerName: "Client",
            renderCell: (row) =>
                row.clientName || clientsMap[row.clientId] || `Client #${row.clientId}`,
        },
        {
            field: "status",
            headerName: "État",
            renderCell: (row) => {
                const status = (row.statusType?.name || "UNKNOWN").toUpperCase();
                const statusConfig = statusOptions.find((s) => s.value === status) || { label: status, color: "default" };
                return <Chip label={statusConfig.label} size="small" color={statusConfig.color} />;
            },
        },
        {
            field: "summary",
            headerName: "Résumé",
            renderCell: (row) => (
                <Tooltip title={row.details || ""} arrow placement="top">
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {row.summary || "—"}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: "expectedSenders",
            headerName: "Expéditeurs attendus",
            renderCell: (row) =>
                Array.isArray(row.expectedSenders) && row.expectedSenders.length > 0
                    ? row.expectedSenders.join(", ")
                    : "—",
        },
        {
            field: "lastEmail",
            headerName: "Dernier email reçu",
            renderCell: (row) =>
                row.lastEmail?.dateReceived
                    ? formatDate(row.lastEmail.dateReceived)
                    : "—",
        },
        {
            field: "checkedAt",
            headerName: "Date d'analyse",
            renderCell: (row) => row.checkedAt ? formatDate(row.checkedAt) : "—",
        },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (row) => (
                <Tooltip title="Voir le dernier email" arrow>
                    <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleViewEmail(row)}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <>
        <div style={{ display: "flex", minHeight: "100vh" }}>
            <Sidebar />
            <Box
                sx={{
                    flex: 1,
                    p: { xs: 2, md: 3 },
                    width: "100%",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
                    Rapports d'analyse
                </Typography>

                {/* Barre de filtres */}
                <Box sx={{ mb: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
                        <TextField
                            size="small"
                            placeholder="Rechercher un job..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: { sm: 220 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            select
                            size="small"
                            label="Client"
                            value={filterClient}
                            onChange={(e) => {
                                setFilterClient(e.target.value);
                                setPage(0);
                            }}
                            sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: { sm: 180 } }}
                        >
                            <MenuItem value="">Tous les clients</MenuItem>
                            {clients.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {c.nom || c.name || `Client #${c.id}`}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="État"
                            value={filterStatus}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                                setPage(0);
                            }}
                            sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: { sm: 160 } }}
                        >
                            <MenuItem value="">Tous les états</MenuItem>
                            {statusOptions.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            type="date"
                            label="Date début"
                            value={filterDateFrom}
                            onChange={(e) => {
                                const v = e.target.value;
                                setFilterDateFrom(v);
                                setFilterDateTo(v);
                                setPage(0);
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: { sm: 170 } }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Date fin"
                            value={filterDateTo}
                            onChange={(e) => {
                                setFilterDateTo(e.target.value);
                                setPage(0);
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: { sm: 170 } }}
                        />
                        <Button
                            size="small"
                            variant="text"
                            color="secondary"
                            onClick={handleResetFilters}
                            sx={{
                                flex: { xs: "1 1 100%", sm: "0 0 auto" },
                                minWidth: { sm: 140 },
                            }}
                        >
                            Réinitialiser
                        </Button>
                    </Stack>
                </Box>

                {loading ? (
                    <Typography color="text.secondary">Chargement...</Typography>
                ) : total === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                        Aucun rapport d'analyse trouvé.
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {total} rapport(s) trouvé(s)
                        </Typography>
                        <GenericDataTable
                            columns={columns}
                            rows={reports}
                            bordered
                            headerAlign="center"
                            cellAlign="center"
                            headerSx={{
                                fontSize: 14,
                                backgroundColor: "primary.main",
                                color: "primary.contrastText",
                            }}
                            cellSx={{ fontSize: 13 }}
                        />
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Lignes par page"
                        />
                    </>
                )}
            </Box>
        </div>

        <EmailDetailModal
            open={emailModalOpen}
            onClose={closeEmailModal}
            jobId={selectedJob?.id}
            jobName={selectedJob?.name}
            reportData={selectedReportData}
            reportId={selectedReportId}
            onUpdated={loadReports}
        />
        </>
    );
}
